'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  gender: 'male\' as \'male\' | \'female',
  height: 70,
  neck: 15,
  waist: 32,
  hip: 38,
};

const PRESETS = {
  conservative: { gender: 'male\' as \'male\' | \'female', height: 68, neck: 14, waist: 30, hip: 36 },
  moderate: { gender: 'male\' as \'male\' | \'female', height: 70, neck: 15, waist: 32, hip: 38 },
  aggressive: { gender: 'male\' as \'male\' | \'female', height: 72, neck: 16, waist: 36, hip: 40 },
};

export default function CalculatorBodyFat() {
  const [gender, setGender] = useState<'male' | 'female'>(INITIAL_STATE.gender);
  const [height, setHeight] = useState(INITIAL_STATE.height);
  const [neck, setNeck] = useState(INITIAL_STATE.neck);
  const [waist, setWaist] = useState(INITIAL_STATE.waist);
  const [hip, setHip] = useState(INITIAL_STATE.hip);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: any) => void, value: any) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const calculateBodyFat = () => {
    if (gender === 'male') {
      return 86.010 * Math.log10(waist - neck) - 70.041 * Math.log10(height) + 36.76;
    } else {
      return 163.205 * Math.log10(waist + hip - neck) - 97.684 * Math.log10(height) - 78.387;
    }
  };

  const bodyFat = calculateBodyFat();

  const getCategory = () => {
    if (gender === 'male') {
      if (bodyFat < 6) return { text: 'Essential Fat', color: 'text-blue-600', bg: 'bg-blue-500' };
      if (bodyFat < 14) return { text: 'Athletes', color: 'text-green-600', bg: 'bg-green-500' };
      if (bodyFat < 18) return { text: 'Fitness', color: 'text-emerald-600', bg: 'bg-emerald-500' };
      if (bodyFat < 25) return { text: 'Average', color: 'text-yellow-600', bg: 'bg-yellow-500' };
      return { text: 'Obese', color: 'text-red-600', bg: 'bg-red-500' };
    } else {
      if (bodyFat < 14) return { text: 'Essential Fat', color: 'text-blue-600', bg: 'bg-blue-500' };
      if (bodyFat < 21) return { text: 'Athletes', color: 'text-green-600', bg: 'bg-green-500' };
      if (bodyFat < 25) return { text: 'Fitness', color: 'text-emerald-600', bg: 'bg-emerald-500' };
      if (bodyFat < 32) return { text: 'Average', color: 'text-yellow-600', bg: 'bg-yellow-500' };
      return { text: 'Obese', color: 'text-red-600', bg: 'bg-red-500' };
    }
  };

  const category = getCategory();

  const handleReset = () => {
    setGender(INITIAL_STATE.gender);
    setHeight(INITIAL_STATE.height);
    setNeck(INITIAL_STATE.neck);
    setWaist(INITIAL_STATE.waist);
    setHip(INITIAL_STATE.hip);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setGender(values.gender);
    setHeight(values.height);
    setNeck(values.neck);
    setWaist(values.waist);
    setHip(values.hip);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button onClick={() => handlePreset('conservative')} className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200">
              Conservative
            </button>
            <button onClick={() => handlePreset('moderate')} className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg font-semibold text-sm transition-all border border-emerald-200">
              Moderate
            </button>
            <button onClick={() => handlePreset('aggressive')} className="px-3 py-2 bg-orange-50 hover:bg-orange-100 text-orange-700 rounded-lg font-semibold text-sm transition-all border border-orange-200">
              Aggressive
            </button>
          </div>
          <button onClick={handleReset} className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all">
            Reset to Default
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Gender</label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => handleChange(setGender, 'male')} className={`px-4 py-2 rounded-lg font-semibold transition-all ${gender === 'male' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                Male
              </button>
              <button onClick={() => handleChange(setGender, 'female')} className={`px-4 py-2 rounded-lg font-semibold transition-all ${gender === 'female' ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
                Female
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Height: {height} inches</label>
            <input type="range" min="48" max="84" value={height} onChange={(e) => handleChange(setHeight, Number(e.target.value))} className="w-full" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Neck: {neck} inches</label>
            <input type="range" min="10" max="24" value={neck} onChange={(e) => handleChange(setNeck, Number(e.target.value))} className="w-full" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Waist: {waist} inches</label>
            <input type="range" min="20" max="60" value={waist} onChange={(e) => handleChange(setWaist, Number(e.target.value))} className="w-full" />
          </div>

          {gender === 'female' && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Hip: {hip} inches</label>
              <input type="range" min="25" max="60" value={hip} onChange={(e) => handleChange(setHip, Number(e.target.value))} className="w-full" />
            </div>
          )}
        </div>

        <div className="mt-8 p-6 bg-gradient-to-br from-slate-50 to-emerald-50 rounded-xl border-2 border-emerald-200">
          <div className="text-center">
            <div className="text-sm font-semibold text-slate-600 mb-2">Estimated Body Fat Percentage</div>
            <div className="text-5xl font-bold text-slate-900 mb-2">{bodyFat.toFixed(1)}%</div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${category.bg} bg-opacity-10 border-2 border-current ${category.color}`}>
              <div className={`w-3 h-3 rounded-full ${category.bg}`}></div>
              <span className="font-semibold">{category.text}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}